/**
 * The category and product pages, restyled to the Horizon mockups (redesign 2026-10-01:
 * `catalogue.html`, `product.html`, scale and geometry from `css/tokens.css`).
 *
 * <p>Those pages are commerce's, shared by every shop, so nothing here changes what they
 * render — only how this shop draws it. The rules hang off the neutral hooks commerce
 * carries for exactly this (`sf-plp*`, `sf-grid`, `sf-card*`, `sf-subcats`, `sf-facets`,
 * `sf-category-rail`, `data-product-*`, `data-multi-buy*`, `data-spec-group`,
 * `data-gallery-hero`) and are scoped under `.ae-skin`, the wrapper this vertical's
 * catalogue surface puts round the route. Every colour is a token (`--ae-*`, defined in
 * TradeStyles BASE_CSS from the tenant's palette), so both Horizon moods — dark default
 * and the day palette held for later — recolour these pages without touching this file.
 *
 * <p><strong>The listing card is no longer one of those pages.</strong> What the mockup asks
 * of a card — the spec microtable, the part number, the datasheet link and the price per watt
 * — is markup, and a stylesheet hung off somebody else's `sf-card*` hooks cannot add a line.
 * The listing API began returning a card's attributes, documents and MPN on 2026-10-01, so
 * the shop draws its own card instead: {@link SolarListingGrid}, registered through the
 * extension's `catalogueComponents.productGrid` and styled by `listingGridCss.ts`. The
 * `sf-grid`/`sf-card*` rules below stay because they still style the platform grid wherever
 * one is rendered outside the two listing routes.
 */
export const CATALOGUE_CSS = `
/* The shop's width, not the platform's 1200px. */
.ae-skin .sf-plp,.ae-skin [data-product-detail]{max-width:var(--ae-max)}

/* Page and section headings on the Horizon h2 scale — the mockups set the catalogue H1,
   the product title and the spec section head all in --fs-h2 at weight 680. */
.ae-skin .sf-plp__title,.ae-skin [data-product-title],.ae-skin [data-product-specs] > h2{
  font-family:inherit !important;font-weight:680 !important;letter-spacing:-.02em;line-height:1.12 !important;
  font-size:clamp(1.625rem,3vw,2.375rem) !important;text-wrap:balance;
}

/* Sections as chips, not a ruled table of links — the sort-pill voice. */
.ae-skin .sf-subcats ul{display:flex !important;flex-wrap:wrap;gap:8px;border:0 !important}
.ae-skin .sf-subcats li{border:0 !important}
.ae-skin .sf-subcats a{min-height:40px !important;padding:8px 14px !important;border:1px solid var(--ae-border-strong);
  border-radius:999px;background:var(--ae-surface) !important;color:var(--ae-ink);font-size:.875rem;font-weight:600;
  text-decoration:none !important}
.ae-skin .sf-subcats a:hover{border-color:var(--ae-muted);color:var(--ae-link)}

/* The rail: filters only. The category tree goes — the department bar under the header
   already reaches every department, and the tree doubled the page's height (27 September
   2026, kept under Horizon: the mockup's sidebar is filters alone). */
.ae-skin .sf-category-rail{display:none}
@media (min-width:1024px){
  .ae-skin .sf-plp__body{grid-template-columns:260px minmax(0,1fr) !important;gap:32px !important}
}

/* Filters. On a phone they sit in a card; from 1024px the card dissolves and the groups
   rule themselves with hairlines, as the mockup's sidebar does. (No align-self:start on
   the card: the rail is a flex column, and that once shrank it to its widest label.) */
.ae-skin .sf-facets{gap:0 !important;padding:4px 16px;border:1px solid var(--ae-border);border-radius:var(--ae-radius-lg);
  background:var(--ae-surface)}
@media (min-width:1024px){
  .ae-skin .sf-facets{border:0;border-radius:0;background:transparent;padding:0}
}
.ae-skin .sf-facets__head{padding:12px 0 10px}
.ae-skin .sf-facets__head h2{font-size:1.0625rem !important;font-weight:650 !important;letter-spacing:-.01em;color:var(--ae-ink)}
.ae-skin .sf-facets__head a{font-size:.8125rem;font-weight:600;color:var(--ae-link)}
.ae-skin .sf-facets__group{padding:14px 0;border-top:1px solid var(--ae-border)}
.ae-skin .sf-facets__group h3{margin:0 0 6px !important;font-size:.75rem !important;font-weight:650 !important;
  text-transform:uppercase;letter-spacing:.08em;color:var(--ae-muted)}
.ae-skin .sf-facets__group ul{gap:0 !important}
/* Each value reads as a checkbox row — it is a link underneath (see FacetPanel), and the box
   is drawn, so it still works before hydration and has a URL. */
.ae-skin .sf-facets__value{display:flex !important;justify-content:flex-start !important;gap:10px !important;
  padding:9px 0 !important;min-height:44px;border-radius:0 !important;font-size:.875rem !important;font-weight:400 !important;
  color:var(--ae-ink) !important;text-decoration:none !important}
.ae-skin .sf-facets__value::before{content:"";flex:none;width:18px;height:18px;border:1.5px solid var(--ae-border-strong);
  border-radius:4px;background:var(--ae-surface);display:grid;place-items:center;font-size:12px;font-weight:700;line-height:1}
.ae-skin .sf-facets__value:hover::before{border-color:var(--ae-brand)}
.ae-skin .sf-facets__value:hover .sf-facets__label{color:var(--ae-link)}
.ae-skin .sf-facets__value[aria-pressed="true"]::before{content:"✓";background:var(--ae-brand);border-color:var(--ae-brand);color:var(--ae-on-brand)}
.ae-skin .sf-facets__value[aria-pressed="true"] .sf-facets__label{font-weight:600}
.ae-skin .sf-facets__count{margin-left:auto;font-size:.75rem;color:var(--ae-muted) !important;font-variant-numeric:tabular-nums}

/* Supplier codes stay off the page until the catalogue carries our own. A "SEG-" code names
   the supplier (owner, 27 September 2026). */
.ae-skin .sf-card__code,.ae-skin [data-product-code]{display:none !important}

/* The toolbar: hairline-ruled, the count in the mono register voice, the sort options as
   pills with the active one in the button ink. */
.ae-skin .sf-plp__toolbar{padding:12px 0;border-block:1px solid var(--ae-border)}
.ae-skin .sf-plp__toolbar > p{font-family:var(--font-family-mono,ui-monospace,SFMono-Regular,Menlo,Consolas,monospace);
  font-size:.6875rem;letter-spacing:.06em;text-transform:uppercase;color:var(--ae-muted) !important;font-variant-numeric:tabular-nums}
.ae-skin .sf-plp__toolbar a{display:inline-flex;align-items:center;min-height:40px;padding:6px 14px;
  border:1px solid var(--ae-border-strong) !important;border-radius:999px;background:var(--ae-surface);
  color:var(--ae-ink) !important;font-size:.8125rem;font-weight:600;text-decoration:none !important}
.ae-skin .sf-plp__toolbar a:hover{border-color:var(--ae-muted) !important}
.ae-skin .sf-plp__toolbar a[aria-current="true"]{background:var(--ae-brand) !important;border-color:var(--ae-brand) !important;
  color:var(--ae-on-brand) !important}

/* Intrinsic card widths respond to the space beside filters, not the viewport alone.
   Price, availability and quantity remain in their original DOM reading order. */
.ae-skin .sf-grid--trade{display:grid !important;grid-template-columns:repeat(auto-fill,minmax(min(100%,240px),1fr)) !important;
  gap:clamp(16px,2vw,24px);border:0 !important;background:transparent;list-style:none;padding:0}
.ae-skin .sf-grid--trade .sf-card{display:flex;flex-direction:column;min-width:0;padding:0 !important;overflow:hidden;
  border:1px solid var(--ae-border) !important;border-radius:var(--ae-radius-lg);background:var(--ae-surface);
  box-shadow:var(--ae-shadow-sm);transition:border-color .18s ease,box-shadow .18s ease,transform .18s ease}
.ae-skin .sf-grid--trade .sf-card:hover{border-color:var(--ae-border-strong) !important;box-shadow:var(--ae-shadow)}
@media (prefers-reduced-motion:no-preference){
  .ae-skin .sf-grid--trade .sf-card:hover{transform:translateY(-3px)}
}
.ae-skin .sf-grid--trade .sf-card__body{display:flex;flex-direction:column;flex:1;min-width:0;padding:16px}
/* The photograph on its own stage: a 4:3 pane over a hairline, the part contained. */
.ae-skin .sf-grid--trade .sf-card__media{aspect-ratio:4/3 !important;width:100%;margin:0 !important;padding:20px;
  border-bottom:1px solid var(--ae-border);border-radius:0;object-fit:contain;overflow:hidden;background:var(--ae-surface) !important}
.ae-skin .sf-card__brand{font-size:.75rem;letter-spacing:.08em;color:var(--ae-muted);margin-bottom:5px}
.ae-skin .sf-card__title{font-size:1.0625rem;line-height:1.35;font-weight:650;letter-spacing:-.01em;margin:0 0 12px;overflow-wrap:anywhere}
.ae-skin .sf-card__title a{color:var(--ae-ink) !important}
.ae-skin .sf-card__title a:hover{color:var(--ae-link) !important}
.ae-skin .sf-card__price{margin-top:auto;padding-top:12px;border-top:1px solid var(--ae-border);text-align:left}
.ae-skin .sf-card__price > div > p:first-child > span:first-child{font-size:1.375rem !important;font-weight:700 !important;
  letter-spacing:-.01em;font-variant-numeric:tabular-nums}
.ae-skin .sf-card__stock{font-size:.8125rem;margin-block:8px 12px}
.ae-skin .sf-card__buy{margin-top:10px}
/* The card's Add is the navy secondary voice — amber is reserved for a page's one primary
   CTA (DESIGN.md §5), which on these routes is the product page's buy button. */
.ae-skin .sf-card__buy button{background:var(--ae-brand) !important;color:var(--ae-on-brand) !important;
  border-radius:var(--ae-radius);font-weight:600;min-height:44px}
.ae-skin .sf-card__buy button:hover:not(:disabled){background:var(--ae-brand-deep) !important;filter:none}
.ae-skin .sf-card__buy input[type="number"]{border-color:var(--ae-border-strong) !important;border-radius:var(--ae-radius);
  background:var(--ae-surface);color:var(--ae-ink);min-height:44px}
.ae-skin .sf-card__buy a{color:var(--ae-link)}

/* Pagination as the mockup's pager: bordered 44px boxes, the current page in the button ink. */
.ae-skin .sf-plp__results > nav a{border-radius:var(--ae-radius);border-color:var(--ae-border-strong) !important;
  background:var(--ae-surface);color:var(--ae-ink);font-weight:600;font-variant-numeric:tabular-nums;text-decoration:none !important}
.ae-skin .sf-plp__results > nav a:hover{border-color:var(--ae-muted) !important}
.ae-skin .sf-plp__results > nav a[aria-current="page"]{background:var(--ae-brand) !important;
  border-color:var(--ae-brand) !important;color:var(--ae-on-brand) !important}

/* Product page, to the mockup: picture and buying side by side at 1.1 : 1, the picture a
   4:3 stage rather than a square the height of the screen. */
@media (min-width:1024px){
  .ae-skin [data-product-layout="media"]{grid-template-columns:minmax(0,1.1fr) minmax(0,1fr) !important;gap:48px !important}
}
.ae-skin [data-product-media] > [role="img"],.ae-skin [data-product-media] [data-gallery-hero] img{
  aspect-ratio:4/3 !important;width:100%;padding:20px;border:1px solid var(--ae-border) !important;
  border-radius:var(--ae-radius-lg);background:var(--ae-surface) !important;object-fit:contain}
.ae-skin [data-product-media] ul img{object-fit:contain;background:var(--ae-surface);border-radius:var(--ae-radius);
  border-color:var(--ae-border-strong) !important}

/* The key facts as the mockup's spec chips: uniform muted pills on the subtle tint. */
.ae-skin [data-product-facts] li{border:1px solid var(--ae-border) !important;background:var(--ae-subtle);
  color:var(--ae-muted);border-radius:999px;padding:3px 10px;font-size:.75rem;font-weight:550}

/* The buy box as a card, the price large, stock in green with a dot. */
.ae-skin [data-product-buy]{border:1px solid var(--ae-border) !important;border-radius:var(--ae-radius-lg);
  background:var(--ae-surface);box-shadow:var(--ae-shadow-sm);padding:24px !important;margin-top:16px}
.ae-skin [data-product-buy] > div:first-child > p:first-child > span:first-child{font-size:1.75rem !important;
  font-weight:700 !important;letter-spacing:-.02em;font-variant-numeric:tabular-nums}
.ae-skin [data-product-stock]{display:flex;align-items:center;gap:8px;font-size:.875rem;color:var(--ae-muted)}
.ae-skin [data-product-stock="IN_STOCK"]{color:var(--ae-success)}
.ae-skin [data-product-stock]::before{content:"";width:9px;height:9px;border-radius:50%;background:currentColor;flex:none}
/* The page's one primary CTA, in the accent: amber fill under the button ink, dusk on hover. */
.ae-skin [data-product-buy] button{background:var(--ae-accent) !important;color:var(--ae-on-accent) !important;
  border-radius:var(--ae-radius);font-weight:650;transition:background .15s ease,transform .15s ease}
.ae-skin [data-product-buy] button:hover:not(:disabled){background:var(--ae-accent-deep) !important;filter:none;transform:translateY(-1px)}
.ae-skin [data-product-buy] input[type="number"]{border-color:var(--ae-border-strong) !important;border-radius:var(--ae-radius);
  background:var(--ae-surface);color:var(--ae-ink);min-height:44px}
.ae-skin [data-product-buy] a{color:var(--ae-link)}

/* Multi-buy, as in the mockup: ruled rows, the saving in the accent's text form, the pallet
   on a tint with the navy secondary button (one amber CTA per page). */
.ae-skin [data-multi-buy-table] caption{color:var(--ae-muted)}
.ae-skin [data-multi-buy-table] tr{border-color:var(--ae-border) !important}
.ae-skin [data-multi-buy-table] td{padding:8px 10px !important;border:0;border-top:1px solid var(--ae-border);
  font-variant-numeric:tabular-nums}
.ae-skin [data-multi-buy-table] tr:last-child td{background:var(--ae-accent-tint)}
.ae-skin [data-multi-buy-saving]{color:var(--ae-accent-ink)}
.ae-skin [data-multi-buy-pallet]{background:var(--ae-subtle) !important;border-radius:var(--ae-radius);color:var(--ae-ink)}
.ae-skin [data-multi-buy-pallet] button{background:var(--ae-brand) !important;color:var(--ae-on-brand) !important;
  border-color:var(--ae-brand) !important;border-radius:var(--ae-radius);transform:none}
.ae-skin [data-multi-buy-pallet] button:hover:not(:disabled){background:var(--ae-brand-deep) !important;
  border-color:var(--ae-brand-deep) !important}

/* Specifications: each group a card, ruled rows, the label column on a tint. */
.ae-skin [data-product-specs] [data-spec-group]{border:1px solid var(--ae-border);border-radius:var(--ae-radius-lg);
  background:var(--ae-surface);box-shadow:var(--ae-shadow-sm);overflow:hidden}
.ae-skin [data-product-specs] [data-spec-group] h3{margin:0 !important;padding:12px 14px 10px;font-size:.75rem !important;
  font-weight:650 !important;text-transform:uppercase;letter-spacing:.08em;color:var(--ae-muted);
  border-bottom:1px solid var(--ae-border)}
.ae-skin [data-product-specs] table{width:100%;border-collapse:collapse;font-size:.875rem}
.ae-skin [data-product-specs] tr{border-color:var(--ae-border) !important}
.ae-skin [data-product-specs] tr:last-child{border-bottom:0 !important}
.ae-skin [data-product-specs] th{width:40%;background:var(--ae-subtle);color:var(--ae-muted);font-weight:400;
  text-align:left;vertical-align:top;padding:9px 14px !important}
.ae-skin [data-product-specs] td{color:var(--ae-ink);font-weight:500;text-align:left !important;vertical-align:top;
  padding:9px 14px !important;font-variant-numeric:tabular-nums}

/* Phone filters: the card becomes a multi-column sheet so seven groups stay one screen. */
@media(max-width:1023px){
  .ae-skin .sf-facets{display:grid !important;grid-template-columns:repeat(auto-fit,minmax(min(100%,190px),1fr));gap:0 24px !important}
  .ae-skin .sf-facets__head{grid-column:1/-1}
  .ae-skin .sf-facets__group ul{max-height:210px;overflow-y:auto;padding:2px}
}
`;
